import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import { makeCRMApi } from "services/CrmApi/CrmApi";
import { ISREditableTypes } from "services/CrmApi/types/types";
import {
  ECrmDirectoryEntryType,
  ICrmCompanyProfile,
  ICrmContactProfile,
  IDirectoryEntry,
} from "services/CrmApi/types/DirectoryEntry";
import * as AuthSelectors from "store/modules/auth/selectors";
import { useCrmDirectoryEntry } from "services/CrmApi/queries/useCrmDirectoryEntry";

const canSREditEntry = (
  isSr: boolean,
  editableContactTypes: string[],
  editableCompanyTypes: string[],
  directoryEntry: IDirectoryEntry | undefined,
  directoryEntryType: ECrmDirectoryEntryType | undefined
): boolean => {
  if (!isSr || !directoryEntry || !directoryEntryType) return true;
  if (directoryEntry.type === ECrmDirectoryEntryType.PERSON) {
    const contactTypeCode = (directoryEntry.profile as ICrmContactProfile).contactType.code;
    return editableContactTypes.includes(contactTypeCode);
  }
  if (directoryEntry.type === ECrmDirectoryEntryType.COMPANY) {
    const companyTypeCode = (directoryEntry.profile as ICrmCompanyProfile).companyType.code;
    return editableCompanyTypes.includes(companyTypeCode);
  }
  return true;
};

export const useSREditableTypes = (isSr: boolean) => {
  const crmApi = makeCRMApi();

  return useQuery<AxiosResponse<ISREditableTypes>>({
    queryKey: ['editableTypes', isSr],
    queryFn: () => crmApi.getSREditableTypes(),
    enabled: isSr,
  });
};

/** Returns whether the current user can edit this directory entry. Non-SR users can always edit; SR users are restricted by contact/company type. */
export const useCanEditEntry = (
  directoryEntryId: string | undefined,
  directoryEntryType: ECrmDirectoryEntryType | undefined
): boolean => {
  const isSr = useSelector(AuthSelectors.isSr);
  const { data } = useSREditableTypes(isSr);
  const { directoryEntry } = useCrmDirectoryEntry(directoryEntryId, {
    enabled: !!directoryEntryId && isSr,
  });

  if (!isSr) return true;

  const editableContactTypes = (data?.data?.editableContactTypes ?? []) as string[];
  const editableCompanyTypes = (data?.data?.editableCompanyTypes ?? []) as string[];
  return canSREditEntry(isSr, editableContactTypes, editableCompanyTypes, directoryEntry, directoryEntryType);
};

/** Returns whether the current user can edit by contact or company type code. Non-SR users can always edit; SR users are restricted by type. */
export const useCanEditByTypeCode = (
  typeCode: string,
  directoryEntryType: ECrmDirectoryEntryType.PERSON | ECrmDirectoryEntryType.COMPANY
): boolean => {
  const isSr = useSelector(AuthSelectors.isSr);
  const { data } = useSREditableTypes(isSr);

  if (!isSr) return true;

  const editableTypes = ((directoryEntryType === ECrmDirectoryEntryType.PERSON
    ? data?.data?.editableContactTypes
    : data?.data?.editableCompanyTypes) ?? []) as string[];
  return editableTypes.includes(typeCode);
};